import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Search, Trash2, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CustomersTab = ({ pin }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [draft, setDraft] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/customers/list`, { pin });
      setItems(data);
      if (selected) setSelected(data.find((c) => c.id === selected.id) || null);
    } catch { toast.error("Kunden laden fehlgeschlagen"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!selected) { setBookings([]); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/customers/${selected.id}/bookings?pin=${pin}`);
        setBookings(data);
      } catch { setBookings([]); }
    })();
    setDraft({
      name: selected.name || "", phone: selected.phone || "", instagram: selected.instagram || "",
      notes: selected.notes || "", allergies: selected.allergies || "", favorites: selected.favorites || "",
    });
  }, [selected, pin]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.toLowerCase();
    return items.filter((c) => c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s) || (c.phone || "").includes(s));
  }, [items, q]);

  const [newFirst, setNewFirst] = useState(""); const [newLast, setNewLast] = useState("");
  const [newEmail, setNewEmail] = useState(""); const [newPhone, setNewPhone] = useState("");
  const [newStreet, setNewStreet] = useState(""); const [newPlz, setNewPlz] = useState(""); const [newCity, setNewCity] = useState("");
  const createManual = async () => {
    const first = newFirst.trim(), last = newLast.trim(), email = newEmail.trim();
    if (first.length < 2 || last.length < 2 || !email) return toast.error("Vorname, Nachname und E-Mail sind Pflicht");
    if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(email)) return toast.error("Bitte eine gültige E-Mail eintragen");
    try {
      const { data } = await axios.post(`${API}/admin/customers/upsert`, {
        pin, name: `${first} ${last}`, email, phone: newPhone.trim(),
        street: newStreet.trim(), postal_code: newPlz.trim(), city: newCity.trim(),
      });
      setNewFirst(""); setNewLast(""); setNewEmail(""); setNewPhone("");
      setNewStreet(""); setNewPlz(""); setNewCity("");
      toast.success("Kunde gespeichert"); load(); setSelected(data);
    } catch { toast.error("Speichern fehlgeschlagen"); }
  };

  const saveDraft = async () => {
    if (!selected || !draft) return;
    try { await axios.patch(`${API}/admin/customers/${selected.id}`, { pin, ...draft }); toast.success("Gespeichert"); load(); }
    catch { toast.error("Speichern fehlgeschlagen"); }
  };
  const del = async () => {
    if (!selected || !window.confirm("Kunde löschen?")) return;
    try { await axios.delete(`${API}/admin/customers/${selected.id}?pin=${pin}`); setSelected(null); load(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  };

  return (
    <div data-testid="tab-customers" className="grid md:grid-cols-[340px_1fr] gap-8">
      <div>
        <div className="flex items-center gap-2 border border-border px-3 py-2 mb-3">
          <Search size={16} className="text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kunden suchen…" data-testid="customer-search" className="flex-1 text-sm outline-none bg-transparent" />
        </div>
        <div className="border border-border divide-y divide-border max-h-[500px] overflow-auto">
          {filtered.map((c) => (
            <button key={c.id} data-testid={`customer-item-${c.id}`}
              onClick={() => setSelected(c)}
              className={`w-full text-left p-3 hover:bg-muted transition-colors ${selected?.id === c.id ? "bg-muted" : ""}`}>
              <div className="text-sm">{c.name}</div>
              <div className="text-xs text-muted-foreground">{c.email}</div>
            </button>
          ))}
          {!filtered.length && <p className="p-4 text-sm text-muted-foreground">Keine Treffer.</p>}
        </div>

        <div className="mt-6 border border-border p-4 space-y-2">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Neu anlegen</p>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Vorname *" value={newFirst} onChange={(e) => setNewFirst(e.target.value)} data-testid="customer-new-first" />
            <Input placeholder="Nachname *" value={newLast} onChange={(e) => setNewLast(e.target.value)} data-testid="customer-new-last" />
          </div>
          <Input placeholder="E-Mail *" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="customer-new-email" />
          <Input placeholder="Telefon" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} data-testid="customer-new-phone" />
          <Input placeholder="Straße + Nr." value={newStreet} onChange={(e) => setNewStreet(e.target.value)} data-testid="customer-new-street" />
          <div className="grid grid-cols-[110px_1fr] gap-2">
            <Input placeholder="PLZ" value={newPlz} onChange={(e) => setNewPlz(e.target.value)} data-testid="customer-new-plz" />
            <Input placeholder="Stadt" value={newCity} onChange={(e) => setNewCity(e.target.value)} data-testid="customer-new-city" />
          </div>
          <button data-testid="customer-add-btn" onClick={createManual} className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-2 text-sm mt-2">
            <Plus size={14} /> Kunde anlegen
          </button>
        </div>
      </div>

      <div>
        {!selected || !draft ? (
          <div className="border border-dashed border-border p-10 text-center text-muted-foreground">Wähle links einen Kunden aus – oder lege einen neuen an.</div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-serif text-2xl">{selected.name}</h3>
              <div className="flex gap-2">
                <button data-testid="customer-save" onClick={saveDraft} className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-sm"><Save size={14} /> Speichern</button>
                <button data-testid="customer-delete" onClick={del} className="flex items-center gap-2 border border-border px-3 py-2 text-sm text-foreground/60 hover:text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-xs text-muted-foreground">Name<Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></label>
              <label className="text-xs text-muted-foreground">Telefon<Input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></label>
              <label className="text-xs text-muted-foreground">Instagram<Input value={draft.instagram} onChange={(e) => setDraft({ ...draft, instagram: e.target.value })} /></label>
              <div className="text-xs text-muted-foreground">E-Mail<div className="mt-2 px-3 py-2 border border-border text-sm text-foreground/60">{selected.email}</div></div>
            </div>
            <div className="grid sm:grid-cols-[1fr_120px_1fr] gap-3">
              <label className="text-xs text-muted-foreground">Straße + Nr.<Input value={draft.street} onChange={(e) => setDraft({ ...draft, street: e.target.value })} data-testid="customer-street" /></label>
              <label className="text-xs text-muted-foreground">PLZ<Input value={draft.postal_code} onChange={(e) => setDraft({ ...draft, postal_code: e.target.value })} data-testid="customer-plz" /></label>
              <label className="text-xs text-muted-foreground">Stadt<Input value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} data-testid="customer-city" /></label>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <label className="text-xs text-muted-foreground">Allergien<Textarea rows={3} value={draft.allergies} onChange={(e) => setDraft({ ...draft, allergies: e.target.value })} /></label>
              <label className="text-xs text-muted-foreground">Lieblingsfarben<Textarea rows={3} value={draft.favorites} onChange={(e) => setDraft({ ...draft, favorites: e.target.value })} /></label>
              <label className="text-xs text-muted-foreground">Interne Notizen<Textarea rows={3} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></label>
            </div>

            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Terminhistorie ({bookings.length})</p>
              <ul className="divide-y divide-border border border-border max-h-[300px] overflow-auto">
                {bookings.map((b) => (
                  <li key={b.id} className="p-3 text-sm flex items-center justify-between">
                    <span>{b.date} · {b.time} · {b.service}</span>
                    <span className="text-xs text-muted-foreground">{b.status}</span>
                  </li>
                ))}
                {!bookings.length && <li className="p-3 text-xs text-muted-foreground">Noch keine Termine.</li>}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
